/**
 * **How a day and a start are WRITTEN on the booking screen** — the agenda's heading, the detail's
 * date line and the free-start chips.
 *
 * The canon writes weekdays without the abbreviation dot and without the comma `Intl` puts after
 * them in Polish (`czw 24 września`, `czw 19:00`), so the parts are read by type and composed here
 * rather than taken whole. Civil days are formatted in UTC at noon, instants on the trainer's zone.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { dateTimeFormat } from '~/shared/lib/intlCache';

import { civilDate, wallClock, wallReading, type CivilDay } from './trainerClock';

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((entry) => entry.type === type)?.value ?? '';
}

/** `czw` — the short weekday of a civil day, with no trailing dot. */
export function shortWeekday(day: CivilDay, locale: string): string {
  const parts = dateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short' }).formatToParts(
    civilDate(day),
  );
  return part(parts, 'weekday').replace(/\.$/, '');
}

/** `czwartek` — the long weekday of a civil day, as the locale writes it mid-sentence. */
export function longWeekday(day: CivilDay, locale: string): string {
  return dateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(civilDate(day));
}

/** `24.09` — day and month, two digits each. */
export function dayMonth(day: CivilDay): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** `czw 24 września` — the detail panel's date line. */
export function weekdayDate(day: CivilDay, locale: string): string {
  const parts = dateTimeFormat(locale, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
  }).formatToParts(civilDate(day));
  return `${shortWeekday(day, locale)} ${part(parts, 'day')} ${part(parts, 'month')}`;
}

/** `czw 19:00` — a start on the trainer's clock, as the free-start chips write it. */
export function weekdayClock(ms: number, zone: string, locale: string): string {
  return `${shortWeekday(wallReading(ms, zone).day, locale)} ${wallClock(ms, zone)}`;
}

/** `śr 23.09, 11:00` — a deadline, as the detail panel's key–value rows write it. */
export function weekdayDateClock(ms: number, zone: string, locale: string): string {
  const { day } = wallReading(ms, zone);
  return `${shortWeekday(day, locale)} ${dayMonth(day)}, ${wallClock(ms, zone)}`;
}

/**
 * `wtorek, 7 lipca 2026` — a day in full, as a screen reader names a day heading. The year and the
 * month are always there, so two cells of adjacent months on one month grid never read alike.
 */
export function fullDate(day: CivilDay, locale: string): string {
  return dateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(civilDate(day));
}

/** `24 października` — the day and its month in words, as a sentence names a date. */
export function dayMonthWords(day: CivilDay, locale: string): string {
  const parts = dateTimeFormat(locale, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
  }).formatToParts(civilDate(day));
  return `${part(parts, 'day')} ${part(parts, 'month')}`;
}

/** `pt 25 wrz` — a day as the settings and the client's picker write a moment's date. */
export function weekdayDayMonth(day: CivilDay, locale: string): string {
  const parts = dateTimeFormat(locale, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).formatToParts(civilDate(day));
  return `${shortWeekday(day, locale)} ${part(parts, 'day')} ${part(parts, 'month').replace(/\.$/, '')}`;
}

/** `pt 25 wrz, 11:00` — a moment on the trainer's clock. */
export function weekdayDayMonthClock(ms: number, zone: string, locale: string): string {
  return `${weekdayDayMonth(wallReading(ms, zone).day, locale)}, ${wallClock(ms, zone)}`;
}

/** `wrzesień – październik 2026` — the months a run of days spans, as the card's month heads it. */
export function monthSpan(first: CivilDay, last: CivilDay, locale: string): string {
  const month = (day: CivilDay) =>
    dateTimeFormat(locale, { timeZone: 'UTC', month: 'long' }).format(civilDate(day));
  const year = last.slice(0, 4);
  if (first.slice(0, 7) === last.slice(0, 7)) return `${month(first)} ${year}`;
  const firstYear = first.slice(0, 4) === year ? '' : ` ${first.slice(0, 4)}`;
  return `${month(first)}${firstYear} – ${month(last)} ${year}`;
}

/**
 * `czas środkowoeuropejski` — a zone as a person names it, in the reader's language: the generic
 * long name the platform knows (no summer or winter in it, since the hours hold all year), or the
 * zone's id when the platform has no name for it. Read at `nowMs`, because a zone that kept no
 * summer time in some year (Warsaw in 1970) is named by its standard time for that year.
 */
export function zoneName(zone: string, locale: string, nowMs: number): string {
  const parts = dateTimeFormat(locale, {
    timeZone: zone,
    timeZoneName: 'longGeneric',
  }).formatToParts(new Date(nowMs));
  return parts.find((entry) => entry.type === 'timeZoneName')?.value ?? zone;
}

/** „1.09.2026" — an instant's day on the trainer's clock, as the ledger's head dates its start. */
export function numericDate(iso: string, zone: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const { day } = wallReading(ms, zone);
  return `${String(Number(day.slice(8, 10)))}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
}

/** „1 września 2026" — an instant's day on the trainer's clock, in a sentence's words. */
export function proseDate(iso: string, zone: string, locale: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const { day } = wallReading(ms, zone);
  return `${dayMonthWords(day, locale)} ${day.slice(0, 4)}`;
}
